/**
 * Prohibited-items acknowledgment control: an inline checkbox + "Script" button
 * that opens the phone-script dialog. Admin staff confirm the prohibited-items
 * list with the customer on their behalf (there's no website checkout to gate
 * it), which is what unblocks reserving an admin order.
 *
 * Self-contained and reusable: drives a single boolean via `acknowledged` /
 * `onChange`, owns its own dialog open-state. Used on the project details page
 * (one acknowledgment for the whole project, cascaded to phases) and intended to
 * replace the inline copy in OrderRightSidebar so the two never drift.
 *
 * The list + script copy come from shared/prohibitedItems so the admin script
 * and the public website acknowledgment stay identical.
 */
import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { DocumentText20Regular } from '@fluentui/react-icons';

import { PROHIBITED_ITEMS } from '../../../shared/prohibitedItems';

const useStyles = makeStyles({
  // Checkbox + "Script" button share a row; wraps when the container is narrow
  // so neither gets clipped.
  ackRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '4px',
  },
  checkboxLabel: {
    fontSize: '13px',
  },
  // Phone-script dialog: muted instruction above a callout block holding the
  // lines the agent reads aloud, then the acknowledgment checkbox.
  scriptIntro: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    marginTop: 0,
    marginBottom: '12px',
  },
  scriptBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('4px'),
    ...shorthands.padding('12px', '14px'),
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
  },
  scriptList: {
    marginTop: 0,
    marginBottom: 0,
    paddingLeft: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  scriptDialogCheckbox: {
    marginTop: '16px',
  },
});

export interface ProhibitedItemsAckProps {
  /** Current acknowledgment state. NULL/undefined renders as unchecked. */
  acknowledged?: boolean | null;
  /** Fired when staff toggle the checkbox (inline or in the dialog). */
  onChange: (checked: boolean) => void;
  /** Disable while a save is in flight. */
  disabled?: boolean;
  /** Label next to the inline checkbox. */
  label?: string;
}

const DEFAULT_LABEL = 'Customer acknowledged';

const ProhibitedItemsAck: React.FC<ProhibitedItemsAckProps> = ({
  acknowledged,
  onChange,
  disabled = false,
  label = DEFAULT_LABEL,
}) => {
  const styles = useStyles();
  const [scriptOpen, setScriptOpen] = useState(false);
  const checked = !!acknowledged;

  return (
    <>
      <div className={styles.ackRow}>
        <Checkbox
          checked={checked}
          onChange={(_, d) => onChange(!!d.checked)}
          disabled={disabled}
          label={<span className={styles.checkboxLabel}>{label}</span>}
        />
        <Button
          size="small"
          appearance="subtle"
          icon={<DocumentText20Regular />}
          onClick={() => setScriptOpen(true)}
        >
          Script
        </Button>
      </div>

      <Dialog open={scriptOpen} onOpenChange={(_, d) => setScriptOpen(d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Prohibited-items phone script</DialogTitle>
            <DialogContent>
              <p className={styles.scriptIntro}>
                Read this to the customer, then check the box once they confirm.
              </p>
              <div className={styles.scriptBlock}>
                <span>
                  “Before I confirm your booking, I need to go over a few items that
                  can’t go in the dumpster — or that may incur additional disposal fees:”
                </span>
                <ul className={styles.scriptList}>
                  {PROHIBITED_ITEMS.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <span>
                  “Do you understand that these items are prohibited or may incur extra
                  fees, and agree not to place them in the dumpster?”
                </span>
              </div>
              {/* Same value as the inline checkbox — toggling either persists
                  the acknowledgment. */}
              <Checkbox
                className={styles.scriptDialogCheckbox}
                checked={checked}
                onChange={(_, d) => onChange(!!d.checked)}
                disabled={disabled}
                label={label}
              />
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => setScriptOpen(false)}>
                Done
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

export default ProhibitedItemsAck;
